import React, { ReactElement } from 'react';
import useStyles from '../../styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../../../components/atoms/translated_text';
import { Translations } from '../../configuration';
import {
  AppDatePicker,
  AppSelect,
  AppTextField,
  AppCheckbox,
  AppRadiogroup,
} from '../../../../../../components/molecules/form_controls';
import GridLayout from '../../../../../../components/molecules/grid_layout';
import { getState } from '../../../../../../redux/store';
import { ReducerKeys } from '../../../../../../redux/reducers';
import { Button, Box } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import RegistrationDetailActions from '../../../../../../redux/reducers/registration_detail_reducer/actions';
import ModalActions from '../../../../../../redux/reducers/gl_modal_reducer/actions';
import { ModalIDs } from '../../../../../../redux/reducers/gl_modal_reducer/types';
import { FixedEstablishmentInput } from '../../model';
import * as RegistrationDetailSelectors from '../../../../../../redux/reducers/registration_detail_reducer/selectors';
import { StateStatus } from 'redux/utils/common';
import { isVatNumberValid } from 'utils/general';

interface FormFields {
  issuingCountry: string | null;
  type: string | null;
  vatNum: string | null;
  companyName: string | null;
  address: string | null;
  number: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  dateOfFirstSupply: string | null;
  fixedEstablishmentDeclaration: string | null;
}

export default function FixedEstablishmentForm(): ReactElement {
  const dispatch = useDispatch();
  const defaultValues = {
    issuingCountry: null,
    type: null,
    vatNum: null,
    companyName: null,
    address: null,
    number: null,
    postalCode: null,
    city: null,
    country: null,
    dateOfFirstSupply: null,
    fixedEstablishmentDeclaration: null,
  };
  // console.log('Render FixedEstablishmentForm');

  const { errors, control, handleSubmit, setError } = useForm<FormFields>({
    defaultValues,
  });
  const classes = useStyles();

  React.useEffect(() => {
    dispatch(RegistrationDetailActions.fetchIdTypesAction());
  }, [dispatch]);

  const onSubmit = (data: any) => {
    if (!isVatNumberValid(data.vatNum) && haveVAT) {
      setError('vatNum', {
        type: 'manual',
        message: `This is an invalid VAT Number`,
      });
      return;
    }

    let newData: Array<FixedEstablishmentInput> =
      new Array<FixedEstablishmentInput>();
    let reduxData = getState(
      ReducerKeys.REGISTRATION_DETAIL_REDUCER
    ).fixedEstablishments;
    let idType = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER).idTypeList
      .data;
    let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
      .memberStateCountries.data;

    let typeName = '';
    let countryCodeId: number = 0;
    let countryCodeDesc: string = 'Country Code';
    for (let type of idType)
      if (type.id_type_id === data.type) {
        typeName = type.id_type;
        break;
      }
    for (let type of idType)
      if (type.code === 'CC') {
        countryCodeId = type.id_type_id;
        countryCodeDesc = type.id_type;
        break;
      }

    for (let item of reduxData) newData.push(item);

    let newFe: FixedEstablishmentInput = {
      fixed_establish_id: 0,
      address_free: data.address,
      building_identifier: data.number,
      cessation_date: data.dateOfFirstSupply,
      city: data.city,
      country_sub_entity: `TODO - ISSUING COUNTRY ID ${data.country}`,
      district_name: 'TODO',
      floor_identifier: 'TODO',
      issuing: countries[Number.parseInt(data.issuingCountry) - 1].country_id,
      issuing_country_name:
        countries[Number.parseInt(data.issuingCountry) - 1].description,
      name: data.companyName,
      other_local_id: haveVAT
        ? data.fixedEstablishmentDeclaration === 'true'
          ? '1'
          : '0'
        : null,
      fe_declaration: haveVAT
        ? data.fixedEstablishmentDeclaration === 'true'
        : true,
      pob: 'TODO POB',
      post_code: data.postalCode,
      start_date: data.dateOfFirstSupply,
      street: data.address,
      suite_identifier: 'TODO',
      vat_id_num: data.vatNum,
      type_id: haveVAT ? data.type : countryCodeId,
      type: haveVAT ? typeName : countryCodeDesc,
      country_id: haveVAT
        ? data.country
        : countries[Number.parseInt(data.issuingCountry) - 1].country_id,
    };
    newData.push(newFe);
    // console.log('NEW', newFe);
    dispatch(RegistrationDetailActions.setFixedEstablishment(newData));
    dispatch(
      ModalActions.closeModalAction({
        modalID: ModalIDs.MODAL_FIXED_ESTABLISHMENT,
      })
    );
  };
  const Placeholder = () => null;
  const [haveVAT, setHaveVAT] = React.useState(true);

  const handleHaveVATChange = (event: any) => {
    const isChecked = event.target.checked;
    setHaveVAT(isChecked);
  };

  interface IElementsProps {
    size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    element: ReactElement;
    id: string;
    rowStyle?: any;
  }

  const elementsArray: IElementsProps[] = [
    {
      element: (
        <AppCheckbox
          name="haveVAT"
          label="I have a VAT Number in the country of establishment"
          error={'haveVAT' in errors}
          control={control}
          rules={{}}
          checked={haveVAT}
          onChange={handleHaveVATChange}
          style={{ marginLeft: '10px' }}
        />
      ),
      size: 12,
      id: 'haveVAT',
    },
    {
      element: renderIssuingCountryField(errors, control),
      size: 12,
      id: 'issuingCountryFE',
    },
    {
      element: fixedEstablishmentDeclarationControl(errors, control),
      size: 12,
      id: 'fixedEstablishmentDeclaration',
    },
    {
      element: renderTypeField(errors, control, haveVAT),
      size: 12,
      id: 'type',
    },
    {
      element: renderVatNumberField(errors, control, haveVAT),
      size: 12,
      id: 'vatNumFE',
    },
    {
      element: renderCompanyNameField(errors, control, haveVAT),
      size: 12,
      id: 'companyNameFE',
    },
    {
      element: renderAddressField(errors, control, haveVAT),
      size: 12,
      id: 'addressFE',
    },
    {
      element: renderHouseNumberField(errors, control, haveVAT),
      size: 6,
      id: 'numberFE',
    },
    {
      element: renderPostalCodeField(errors, control, haveVAT),
      size: 6,
      id: 'postalcodeFE',
    },
    {
      element: renderCityField(errors, control, haveVAT),
      size: 6,
      id: 'cityFE',
    },
    {
      element: renderCountryField(errors, control, haveVAT),
      size: 6,
      id: 'countryFE',
    },
    {
      element: renderDateOfFirstSupplyField(errors, control),
      size: 12,
      id: 'dateOfFirstSupplyFE',
    },
    {
      element: (
        <Button
          variant="contained"
          color="primary"
          type="submit"
          style={{ width: '100%' }}
        >
          <TranslatedText textMap={Translations.add} />
        </Button>
      ),
      size: 12,
      id: 'action',
    },
  ];

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={
          haveVAT
            ? elementsArray
            : elementsArray.filter(
                (item) =>
                  item.id === 'issuingCountryFE' ||
                  item.id === 'haveVAT' ||
                  item.id === 'dateOfFirstSupplyFE' ||
                  item.id === 'action'
              )
        }
      />
    </form>
  );
}

const renderIssuingCountryField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  // let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
  //   .nonMemberStateCountries.data;
  let countries = RegistrationDetailSelectors.useGetCountriesMemberState();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: Translations.issuing_country_label.English,
  });
  if (countries && countries.length > 0)
    countries.map((country: any, index: number) => {
      select_countries.push({
        id: index + 1,
        label: country.description,
      });
    });
  // for (let country of countries)

  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="issuingCountry"
      label={<TranslatedText textMap={Translations.issuing_country_label} />}
      error={'issuingCountry' in errors}
      name="issuingCountry"
      control={control}
      rules={{ required: 'Required Field' }}
      options={select_countries as any}
      helperText={errors.issuingCountry && errors.issuingCountry.message}
    />
  );
};

const renderTypeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  const types = RegistrationDetailSelectors.useGetIdTypes();
  let types_options = [];
  types_options.push({
    id: null,
    label: Translations.type_label.English,
  });
  if (types.status === StateStatus.Success && types.data.length > 0) {
    for (let type of types.data) {
      if (type.code !== 'CC') {
        types_options.push({
          id: type.id_type_id,
          label: type.id_type,
        });
      }
    }
  }
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required={haveVAT}
      fullWidth
      id="type"
      label={<TranslatedText textMap={Translations.type_label} />}
      error={'type' in errors}
      name="type"
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      options={types_options as any}
      helperText={errors.type && errors.type.message}
    />
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean // Add the haveVAT prop
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="vatNum"
      required={haveVAT}
      label={<TranslatedText textMap={Translations.vat_number_label} />}
      error={'vatNum' in errors}
      name="vatNum"
      control={control}
      rules={
        haveVAT
          ? {
              required: 'Required Field',
              maxLength: { value: 12, message: 'Maximum length 12' },
            }
          : {}
      }
      helperText={errors.vatNum && errors.vatNum.message}
    />
  );
};

const renderCompanyNameField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required={haveVAT}
      fullWidth
      id="companyName"
      label={<TranslatedText textMap={Translations.company_name_label} />}
      error={'companyName' in errors}
      name="companyName"
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      helperText={errors.companyName && errors.companyName.message}
    />
  );
};

const renderAddressField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required={haveVAT}
      fullWidth
      id="address"
      label={<TranslatedText textMap={Translations.address_street_label} />}
      error={'address' in errors}
      name="address"
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      helperText={errors.address && errors.address.message}
    />
  );
};

const renderHouseNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required={haveVAT}
      fullWidth
      id="number"
      label={<TranslatedText textMap={Translations.number_label} />}
      error={'number' in errors}
      name="number"
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      helperText={errors.number && errors.number.message}
    />
  );
};

const renderPostalCodeField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required={haveVAT}
      fullWidth
      id="postalCode"
      label={<TranslatedText textMap={Translations.postal_code_label} />}
      error={'postalCode' in errors}
      name="postalCode"
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      helperText={errors.postalCode && errors.postalCode.message}
    />
  );
};

const renderCityField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.city_label} />}
      id="city"
      name="city"
      required={haveVAT}
      error={'city' in errors}
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      helperText={errors.city && errors.city.message}
    />
  );
};

const renderCountryField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  haveVAT: boolean
) => {
  // let countries = getState(ReducerKeys.REGISTRATION_DETAIL_REDUCER)
  //   .nonMemberStateCountries.data;
  let countries = RegistrationDetailSelectors.useGetAllCountries();
  let select_countries = [];
  select_countries.push({
    id: null,
    label: Translations.country_label.English,
  });
  if (countries && countries.length > 0)
    for (let country of countries)
      select_countries.push({
        id: country.country_id,
        label: country.description,
      });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      fullWidth
      label={<TranslatedText textMap={Translations.country_label} />}
      id="country"
      name="country"
      required={haveVAT}
      error={'country' in errors}
      control={control}
      rules={haveVAT ? { required: 'Required Field' } : {}}
      options={select_countries as any}
      helperText={errors.country && errors.country.message}
    />
  );
};

const renderDateOfFirstSupplyField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppDatePicker
      fullWidth
      margin="normal"
      label={
        <TranslatedText textMap={Translations.date_of_first_supply_label} />
      }
      autoOk
      error={'dateOfFirstSupply' in errors}
      control={control}
      rules={{ required: 'Required Field' }}
      name="dateOfFirstSupply"
      helperText={errors.dateOfFirstSupply && errors.dateOfFirstSupply.message}
    />
  );
};

const fixedEstablishmentDeclarationControl = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppRadiogroup
      row
      id="fixedEstablishmentDeclaration"
      label=""
      error={'fixedEstablishmentDeclaration' in errors}
      name="fixedEstablishmentDeclaration"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.fixedEstablishmentDeclaration &&
        errors.fixedEstablishmentDeclaration.message
      }
      values={[
        {
          value: 'true',
          label: (
            <TranslatedText
              textMap={Translations.fixedEstablishmentDeclarationValue1}
            />
          ),
        },
        {
          value: 'false',
          label: (
            <TranslatedText
              textMap={Translations.fixedEstablishmentDeclarationValue2}
            />
          ),
        },
      ]}
    />
  );
};
